import { useHover } from '@fullstory/react-hooks';
import { classNames } from '@fullstory/util/fmt';
import React, { forwardRef, PropsWithChildren, useContext } from 'react';
import { Box } from '../../legacy/Box';
import { Middot } from '../../legacy/Middot';
import { identifyComponent } from '../../util';
import { InlineLink } from '../InlineLink';
import { ListContext, ListContextProps } from '../List/context';
import { ListItem } from '../ListItem';
import { Text } from '../Text';
import css from './index.module.scss';

interface ListHeaderAction {
  label: string;
  onClick: () => void;
}

export interface ListHeaderProps extends ListContextProps {
  /**
   * If true, the list header will fix itself to the top of the closest ancestor element
   * that is position: relative when scrolling. It uses position: sticky to handle this
   * functionality.
   */
  fixed?: boolean;

  /**
   * If actions are provided, an InfoList will appear to the right of the List Header
   */
  actions?: ListHeaderAction[];

  /**
   * Darkens the text color on hover and hides actions when not hovered
   */
  hoverable?: boolean;
}

export const ListHeader = forwardRef<HTMLLIElement, PropsWithChildren<ListHeaderProps>>(
  (
    { children, fixed = false, actions, hoverable = false, compact: injectedCompact, ...rest },
    ref,
  ) => {
    const listContext = useContext(ListContext);
    const compact = injectedCompact ?? listContext.compact;
    const { hovered, hoverProps } = useHover();
    const shouldShowActions = !hoverable || hovered;

    return (
      <ListItem
        data-component="ListHeader"
        {...rest}
        {...identifyComponent('list-header', rest)}
        {...(hoverable ? hoverProps : null)} // Don't attach event listeners unless hoverable is true
        className={classNames({ [css.fixed]: fixed })}
        ref={ref}
      >
        <div className={css.listHeader}>
          <Text
            appearance={compact ? 'bodyXSmallBold' : 'bodySmallBold'}
            color={shouldShowActions ? 'default' : 'secondary'}
          >
            {children}
          </Text>
          {actions && actions.length > 0 && (
            <Box className={shouldShowActions ? css.actionsVisible : css.actionsHidden}>
              <Text appearance={compact ? 'bodyXSmall' : 'bodySmall'} align="right">
                {actions.map((action, i) => (
                  <React.Fragment key={action.label}>
                    {i !== 0 && <Middot />}
                    <InlineLink onClick={action.onClick}>{action.label}</InlineLink>
                  </React.Fragment>
                ))}
              </Text>
            </Box>
          )}
        </div>
      </ListItem>
    );
  },
);
ListHeader.displayName = 'ListHeader';
